import { useMemo } from "react";
import {
  MATCH_EVIDENCE_COL,
  countEvidence,
  legendFor,
  type EvidenceLegendEntry,
} from "../lib/matchEvidence";
import type { FrameRecords } from "../types/api";

interface Props {
  // The frames actually rendered beneath this legend. A paired frame carries the
  // column twice ("cri::match_evidence" / "ins::match_evidence"); both are counted,
  // which is what makes the numbers foot to the rows on screen.
  frames: FrameRecords[];
  // The server's vocabulary (result.match_evidence_legend): wording, glyph, order.
  vocabulary?: EvidenceLegendEntry[];
}

// "How these matched" — the plain-English key to the Matched By chips, sitting above
// the grid it describes. Mirrors the legend band reporting/export.py writes at the top
// of every paired Matched/G3 sheet, from the same server-side vocabulary, so the
// workbook and the screen can never explain a match differently.
//
// Renders nothing when the rows below carry no evidence column, or when the server did
// not send a vocabulary — the grid is simply unchanged, never half-explained.
export default function MatchEvidenceLegend({ frames, vocabulary }: Props) {
  const entries = useMemo(() => {
    if (!vocabulary || !vocabulary.length) return [];
    const counts = new Map<string, number>();
    for (const frame of frames) {
      if (!frame) continue;
      frame.columns.forEach((col, idx) => {
        if (!col.endsWith(MATCH_EVIDENCE_COL)) return;
        for (const [label, n] of countEvidence(frame.rows, idx)) {
          counts.set(label, (counts.get(label) ?? 0) + n);
        }
      });
    }
    return legendFor(vocabulary, counts);
  }, [frames, vocabulary]);

  if (!entries.length) return null;

  return (
    <div className="mevi-legend">
      <div className="mevi-legend-title">How these matched</div>
      <ul className="mevi-legend-list">
        {entries.map((e) => (
          <li key={e.label} className={`mevi-legend-item mevi-${e.kind}`}>
            <span className="mevi-legend-chip">
              <span className="mevi-legend-glyph" aria-hidden="true">{e.symbol}</span>
              {e.label}
            </span>
            <span className="mevi-legend-count">{e.rows.toLocaleString()}</span>
            <span className="mevi-legend-text">{e.sentence}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
